// Hooks + fetcher for the TopBar notifications dropdown.
//
// Backend gap (2026-04-26): the notifications router has NO list-of-
// notifications endpoint and NO read/unread state. The current model
// (CaseAlert) holds the outbound mail deliveries (`digest`, `critical`)
// and the form notices (`form_update`, never sent, so `delivered` is
// null) without a `read`, `unread`, or `link` field.
//
// We surface GET /api/notifications/history (the last 25 rows) as the
// dropdown content, and track read-state client-side in localStorage
// keyed by notification id. The shape is purposely small so swapping in
// a real in-app-notification model later (with `read`, `link`, etc.)
// only requires a fetcher change.

import { useCallback, useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  NotificationHistoryItem,
  NotificationHistoryResponse,
  NotificationSettings,
  NotificationSettingsPatch,
  NotificationSettingsResponse,
} from "@duralex/shared";
import { getAuthHeaders } from "./auth";

const READ_STATE_KEY = "duralex:notifications:read";

export async function fetchNotificationHistory(): Promise<NotificationHistoryResponse> {
  const res = await fetch("/api/notifications/history", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`fetchNotificationHistory: ${res.status}`);
  return (await res.json()) as NotificationHistoryResponse;
}

export async function fetchNotificationSettings(): Promise<NotificationSettingsResponse> {
  const res = await fetch("/api/notifications/settings", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`fetchNotificationSettings: ${res.status}`);
  return (await res.json()) as NotificationSettingsResponse;
}

export async function updateNotificationSettings(
  patch: NotificationSettingsPatch,
): Promise<NotificationSettings> {
  const res = await fetch("/api/notifications/settings", {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`updateNotificationSettings: ${res.status}`);
  return (await res.json()) as NotificationSettings;
}

const SETTINGS_KEY = ["notifications", "settings"] as const;

export function useNotificationSettings(enabled = true) {
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: fetchNotificationSettings,
    staleTime: 30_000,
    enabled,
  });
}

/**
 * Optimistic per the web rules: apply the patch to the cached settings
 * immediately, reconcile with the server response, roll back on error.
 */
export function useUpdateNotificationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateNotificationSettings,
    onMutate: async (patch: NotificationSettingsPatch) => {
      await qc.cancelQueries({ queryKey: SETTINGS_KEY });
      const prev = qc.getQueryData<NotificationSettingsResponse>(SETTINGS_KEY);
      if (prev) qc.setQueryData(SETTINGS_KEY, { ...prev, ...patch });
      return { prev };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.prev) qc.setQueryData(SETTINGS_KEY, ctx.prev);
    },
    onSuccess: (updated) => {
      qc.setQueryData<NotificationSettingsResponse>(SETTINGS_KEY, (cur) =>
        cur ? { ...cur, ...updated } : undefined,
      );
    },
  });
}

// ---------------------------------------------------------------------------
// Read-state — localStorage tier (until backend supports it)
// ---------------------------------------------------------------------------

function loadReadIds(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(READ_STATE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x): x is string => typeof x === "string"));
  } catch {
    return new Set();
  }
}

function saveReadIds(ids: Set<string>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(READ_STATE_KEY, JSON.stringify(Array.from(ids)));
    // Notify other tabs / hooks in this tab.
    window.dispatchEvent(new CustomEvent("duralex:notifications:read-changed"));
  } catch {
    /* localStorage can throw in private mode — silently ignore */
  }
}

/**
 * Reactive read-state hook. Returns the set of locally-marked-read ids
 * and a setter that persists + broadcasts the change.
 */
function useReadIds(): {
  readIds: Set<string>;
  markRead: (id: string) => void;
  markAllRead: (ids: string[]) => void;
} {
  const [readIds, setReadIds] = useState<Set<string>>(() => loadReadIds());

  useEffect(() => {
    const onChange = () => setReadIds(loadReadIds());
    if (typeof window === "undefined") return;
    window.addEventListener("duralex:notifications:read-changed", onChange);
    window.addEventListener("storage", (e) => {
      if (e.key === READ_STATE_KEY) onChange();
    });
    return () => {
      window.removeEventListener("duralex:notifications:read-changed", onChange);
    };
  }, []);

  const markRead = useCallback((id: string) => {
    const next = new Set(loadReadIds());
    next.add(id);
    saveReadIds(next);
  }, []);

  const markAllRead = useCallback((ids: string[]) => {
    const next = new Set(loadReadIds());
    for (const id of ids) next.add(id);
    saveReadIds(next);
  }, []);

  return { readIds, markRead, markAllRead };
}

// ---------------------------------------------------------------------------
// Public hooks
// ---------------------------------------------------------------------------

export interface UseNotificationsOpts {
  /** When true, only return items the user has not yet locally dismissed. */
  unreadOnly?: boolean;
  /** Re-fetch interval in ms. Default: 60s. Set to 0/false to disable. */
  refetchInterval?: number | false;
}

export interface NotificationItemView extends NotificationHistoryItem {
  read: boolean;
}

export interface UseNotificationsResult {
  items: NotificationItemView[];
  unreadCount: number;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

export function useNotifications(opts: UseNotificationsOpts = {}): UseNotificationsResult {
  const refetchInterval = opts.refetchInterval === undefined ? 60_000 : opts.refetchInterval;

  const query = useQuery({
    queryKey: ["notifications", "history"],
    queryFn: fetchNotificationHistory,
    staleTime: 30_000,
    refetchInterval,
  });

  const { readIds } = useReadIds();
  const allItems = query.data?.history ?? [];
  const decorated: NotificationItemView[] = allItems.map((it) => ({
    ...it,
    read: readIds.has(it.id),
  }));
  const items = opts.unreadOnly ? decorated.filter((d) => !d.read) : decorated;
  const unreadCount = decorated.filter((d) => !d.read).length;

  return {
    items,
    unreadCount,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}

/**
 * Mark a single notification read. Currently localStorage-only. When
 * the backend grows POST /api/notifications/:id/read, swap the
 * mutationFn body and the UI is unchanged.
 */
export function useMarkNotificationRead() {
  const qc = useQueryClient();
  const { markRead } = useReadIds();
  return useMutation({
    mutationFn: async (id: string) => {
      // TODO(backend): POST /api/notifications/:id/read once the
      // server supports per-notification read state. For now, persist
      // client-side so the bell badge reflects the user's intent.
      markRead(id);
      return { id };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications", "history"] });
    },
  });
}

/**
 * Mark every loaded notification read. localStorage-only for now —
 * see useMarkNotificationRead.
 */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  const { markAllRead } = useReadIds();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      // TODO(backend): POST /api/notifications/read-all once supported.
      markAllRead(ids);
      return { count: ids.length };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications", "history"] });
    },
  });
}
